import {
  Build as BuildIcon,
  Code as CodeIcon,
  ExpandLess,
  ExpandMore,
  Psychology as PsychologyIcon,
} from "@mui/icons-material"
import {
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  styled,
  Typography,
} from "@mui/material"
import "katex/dist/katex.min.css"
import type React from "react"
import { useCallback, useState } from "react"
import ReactMarkdown from "react-markdown"
import rehypeKatex from "rehype-katex"
import remarkGfm from "remark-gfm"
import remarkMath from "remark-math"
import { extraColors } from "../../utils/theme"
import type { ChatPart, PartType } from "./types"

// --- STYLES & CONSTANTS ---

// Known root agent entity ids. Anything not in this set is treated as a
// sub-agent or tool. Keep this in sync with the BE — phoenix#1200's
// headless workflow path emits parts with `entity_id="agent_epidemiologist"`
// for the master run; phoenix#1196's `is_injected` metadata doesn't change
// the entity_id field. If a v1.2 RBAC workflow lands a new root id, add
// it here.
//
// Critically, we do NOT use `role === "model"` as evidence of root —
// every sub-agent's text reply also has role="model" and using it as a
// fallback was silently bucketing sub-agent text into the root pane.
const ROOT_ENTITY_IDS: ReadonlySet<string> = new Set([
  "agent_epidemiologist",
  "agent_assistant",
])

const DEFAULT_ROOT_ENTITY_ID = "agent_epidemiologist"

const isRootEntity = (entityId: string | undefined): boolean =>
  ROOT_ENTITY_IDS.has(entityId ?? DEFAULT_ROOT_ENTITY_ID)

// Centralized so the stats counter and the renderer agree on what
// counts as a tool. Substring `.includes("tool")` was the previous
// approach and would silently misclassify any future part type with
// "tool" in its name (e.g. `agent_transfer_tool`).
const TOOL_PART_TYPES: ReadonlySet<PartType> = new Set<PartType>([
  "tool_call",
  "tool_response",
  "agent_transfer",
])

const PartColorMap: Partial<Record<PartType, string>> = {
  thought: extraColors.status.navyLight,
  tool_call: extraColors.purpleLight,
  tool_response: extraColors.purpleLight,
  code_execution: extraColors.status.yellowMiddle,
  code_result: extraColors.status.yellowMiddle,
  agent_transfer: extraColors.status.navyLight,
}

const MarkdownContent = styled(Box)({
  color: extraColors.white,
  "& p": { margin: 0, marginBottom: "0.5em" },
  "& p:last-child": { marginBottom: 0 },
  "& table": {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: "1rem",
  },
  "& th, & td": {
    border: "1px solid rgba(255, 255, 255, 0.1)",
    padding: "8px 12px",
    textAlign: "left",
  },
  "& th": {
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    fontWeight: "bold",
  },
})

const CodeBlock = styled(Box)({
  padding: "12px",
  backgroundColor: "#0d0d0d",
  borderRadius: "4px",
  fontFamily: "monospace",
  fontSize: "0.85rem",
  color: "#d4d4d4",
  border: `1px solid #333`,
  overflowX: "auto",
  marginBottom: "8px",
})

const MetaBlock = styled(Box)({
  marginTop: "8px",
  padding: "8px",
  backgroundColor: "rgba(0,0,0,0.2)",
  borderRadius: "4px",
  fontFamily: "monospace",
  fontSize: "0.75rem",
  overflowX: "auto",
})

// Stable per-part React key. `index` can legitimately be 0 so we use
// `??`, not `||`. Falls back to a (group, position) composite when the
// BE didn't supply a part_id at all.
const partKey = (part: ChatPart, groupKey: string, position: number): string =>
  part.part_id ?? `${groupKey}:${part.index ?? position}`

// --- HELPER COMPONENTS ---

const ThoughtPart: React.FC<{ part: ChatPart }> = ({ part }) => {
  const isCode = part.type === "code_execution" || part.type === "code_result"
  const isTool = TOOL_PART_TYPES.has(part.type)
  const isThought = part.type === "thought"

  let displayContent: string = part.content
  if (isTool) {
    try {
      const parsed: unknown = JSON.parse(displayContent)
      displayContent = JSON.stringify(parsed, null, 2)
    } catch {
      // already a string; keep as-is
    }
  }

  const hasMetadata =
    part.metadata != null &&
    typeof part.metadata === "object" &&
    Object.keys(part.metadata as object).length > 0

  return (
    <Box
      sx={{
        mb: 2,
        pl: 2,
        borderLeft: `2px solid ${PartColorMap[part.type] ?? "#555"}`,
      }}
    >
      {!isThought && (
        <Typography
          variant="caption"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: extraColors.medium,
            fontWeight: "bold",
            fontSize: "0.65rem",
            letterSpacing: "0.05em",
            mb: 0.5,
            textTransform: "uppercase",
          }}
        >
          {isTool && <BuildIcon sx={{ fontSize: 12 }} />}
          {isCode && <CodeIcon sx={{ fontSize: 12 }} />}
          {part.type.replace("_", " ")}
        </Typography>
      )}

      {isCode || isTool ? (
        <CodeBlock>
          <pre style={{ margin: 0 }}>{displayContent}</pre>
        </CodeBlock>
      ) : (
        <MarkdownContent>
          <ReactMarkdown
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeKatex]}
          >
            {displayContent}
          </ReactMarkdown>
        </MarkdownContent>
      )}

      {hasMetadata && (
        <MetaBlock>
          <pre style={{ margin: 0 }}>
            {JSON.stringify(part.metadata, null, 2)}
          </pre>
        </MetaBlock>
      )}
    </Box>
  )
}

const AgentTransferPart: React.FC<{ part: ChatPart }> = ({ part }) => {
  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        bgcolor: "rgba(100, 150, 255, 0.1)",
        borderLeft: `4px solid ${extraColors.status.navyLight}`,
        borderRadius: 1,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: "bold",
          color: extraColors.status.navyLight,
          textTransform: "uppercase",
          mb: 1,
          display: "block",
        }}
      >
        Context Hand-Off
      </Typography>
      <CodeBlock>
        <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{part.content}</pre>
      </CodeBlock>
    </Box>
  )
}

// Renders a single part. Extracted so the root branch and the nested
// branch can't drift apart on what an `agent_transfer` looks like.
const renderPart = (
  part: ChatPart,
  groupKey: string,
  position: number
): React.ReactNode => {
  const key = partKey(part, groupKey, position)
  if (part.type === "agent_transfer") {
    return <AgentTransferPart key={key} part={part} />
  }
  return <ThoughtPart key={key} part={part} />
}

interface AssessmentGroup {
  entityId: string
  isRoot: boolean
  parts: ChatPart[]
}

const NestedProcessGroup: React.FC<{
  entityId: string
  parts: ChatPart[]
  expanded: boolean
  onToggle: () => void
}> = ({ entityId, parts, expanded, onToggle }) => {
  const hrName = parts[0]?.human_readable_name
  let displayName = entityId
  if (hrName) {
    const prefix = entityId.startsWith("agent_") ? "SUB-AGENT: " : "TOOL: "
    displayName = `${prefix}${hrName} (${entityId})`
  } else if (entityId.startsWith("agent_")) {
    displayName = entityId.replace("agent_", "Sub-Agent: ")
  } else if (entityId.startsWith("tool_")) {
    displayName = entityId.replace("tool_", "Tool: ")
  } else {
    displayName = `Unknown: ${entityId}`
  }

  return (
    <Box
      sx={{
        mb: 1,
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 1,
        overflow: "hidden",
        ml: 2,
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          px: 2,
          cursor: "pointer",
          bgcolor: "rgba(255,255,255,0.03)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: extraColors.purpleLight,
            fontWeight: "bold",
            display: "flex",
            alignItems: "center",
            gap: 1,
            textTransform: "uppercase",
            letterSpacing: "0.05em",
          }}
        >
          {displayName}
        </Typography>
        <IconButton size="small" sx={{ p: 0.5, color: extraColors.medium }}>
          {expanded ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </IconButton>
      </Box>
      {/* Note: not `unmountOnExit` — we want the inner state of any
          rendered children to survive a collapse so the user doesn't
          lose scroll position or per-part expansion when toggling. */}
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.2)" }}>
          {parts.map((p, i) => renderPart(p, `nested:${entityId}`, i))}
        </Box>
      </Collapse>
    </Box>
  )
}

const TopLevelAssessmentProcess: React.FC<{
  groups: AssessmentGroup[]
  isRunning: boolean
}> = ({ groups, isRunning }) => {
  // Default expanded while the agent is running so the user can watch
  // the trace stream in. Collapsed once complete so the chat returns to
  // its tidy default.
  const [expanded, setExpanded] = useState<boolean>(isRunning)
  // Lift nested expansion state up here, keyed by entity_id, so a
  // top-level collapse/expand cycle doesn't wipe each sub-group's
  // open/closed state. Default-collapsed for sub-groups.
  const [nestedExpanded, setNestedExpanded] = useState<Record<string, boolean>>(
    {}
  )
  const toggleNested = useCallback((entityId: string) => {
    setNestedExpanded(prev => ({ ...prev, [entityId]: !prev[entityId] }))
  }, [])

  if (groups.length === 0 && !isRunning) return null

  let thoughtCount = 0
  let toolCount = 0
  for (const g of groups) {
    for (const p of g.parts) {
      if (p.type === "thought") thoughtCount++
      else if (TOOL_PART_TYPES.has(p.type)) toolCount++
    }
  }
  const subAgentCount = groups.filter(g => !g.isRoot).length

  const statParts: string[] = []
  if (thoughtCount > 0) statParts.push(`${thoughtCount} steps`)
  if (toolCount > 0) statParts.push(`${toolCount} tools`)
  if (subAgentCount > 0) statParts.push(`${subAgentCount} agents`)
  const stats = statParts.length > 0 ? ` (${statParts.join(", ")})` : ""
  const summary = isRunning
    ? `Assessment in progress…${stats}`
    : `Assessment Process${stats}`

  return (
    <Box
      sx={{
        mb: 2,
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 1,
        overflow: "hidden",
      }}
    >
      <Box
        onClick={() => setExpanded(!expanded)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          p: 1,
          px: 2,
          cursor: "pointer",
          bgcolor: "rgba(255,255,255,0.03)",
          "&:hover": { bgcolor: "rgba(255,255,255,0.06)" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {isRunning ? (
            <CircularProgress
              size={14}
              sx={{ color: extraColors.status.navyLight }}
            />
          ) : (
            <PsychologyIcon sx={{ fontSize: 16, color: extraColors.medium }} />
          )}
          <Typography
            variant="body2"
            sx={{
              color: extraColors.medium,
              fontSize: "0.85rem",
              fontWeight: "bold",
            }}
          >
            {summary}
          </Typography>
        </Box>
        <IconButton size="small" sx={{ p: 0.5, color: extraColors.medium }}>
          {expanded ? (
            <ExpandLess fontSize="small" />
          ) : (
            <ExpandMore fontSize="small" />
          )}
        </IconButton>
      </Box>
      {/* Same reasoning as the nested Collapse: keep mounted so nested
          expansion state isn't reset on collapse. */}
      <Collapse in={expanded} timeout="auto">
        <Box
          sx={{
            p: 2,
            bgcolor: "rgba(0,0,0,0.2)",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {groups.map(group => {
            if (group.isRoot) {
              return (
                <Box
                  key={`root:${group.entityId}`}
                  sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                >
                  {group.parts.map((part, partIdx) =>
                    renderPart(part, `root:${group.entityId}`, partIdx)
                  )}
                </Box>
              )
            }
            return (
              <NestedProcessGroup
                key={`sub:${group.entityId}`}
                entityId={group.entityId}
                parts={group.parts}
                expanded={nestedExpanded[group.entityId] ?? false}
                onToggle={() => toggleNested(group.entityId)}
              />
            )
          })}
          {isRunning && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                mt: 1,
                pl: 2,
                opacity: 0.7,
              }}
            >
              <CircularProgress size={10} />
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Investigating…
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  )
}

export const AgentResponse: React.FC<{
  parts: ChatPart[]
  isComplete?: boolean
}> = ({ parts, isComplete }) => {
  if (!parts.length) return null

  // 1. Sort all parts chronologically. `index` can legitimately be 0,
  //    so use `??` for the tiebreak — `||` falses out the first event
  //    in a millisecond bucket.
  const sorted = [...parts].sort((a, b) => {
    const tA = a.timestamp ? new Date(a.timestamp).getTime() : 0
    const tB = b.timestamp ? new Date(b.timestamp).getTime() : 0
    const validTA = isNaN(tA) ? 0 : tA
    const validTB = isNaN(tB) ? 0 : tB
    if (validTA === validTB) {
      return (a.index ?? 0) - (b.index ?? 0)
    }
    return validTA - validTB
  })

  // 2. Separate root text from everything else. Root detection is
  //    entity-id-based ONLY: see ROOT_ENTITY_IDS. We deliberately do
  //    NOT fall back to `role === "model"`, which would silently
  //    promote sub-agent text into the root pane (every sub-agent
  //    reply also carries role="model").
  const assessmentParts: ChatPart[] = []
  const rootTextParts: ChatPart[] = []

  for (const part of sorted) {
    const entityId = part.entity_id ?? DEFAULT_ROOT_ENTITY_ID
    if (isRootEntity(entityId) && part.type === "text") {
      rootTextParts.push(part)
    } else {
      assessmentParts.push(part)
    }
  }

  // 3. Group assessment parts by entity_id. Unlike the previous
  //    "merge only if adjacent" approach, this collapses interleaved
  //    runs from the same entity into a single group — so a sub-agent
  //    that yields control back to the root and then emits more events
  //    still renders as ONE collapsible sub-group, not five. We
  //    preserve first-seen order to keep the visual stable as events
  //    stream in.
  const groupIndexByEntity = new Map<string, number>()
  const assessmentGroups: AssessmentGroup[] = []
  for (const part of assessmentParts) {
    const entityId = part.entity_id ?? DEFAULT_ROOT_ENTITY_ID
    const existingIdx = groupIndexByEntity.get(entityId)
    const existingGroup =
      existingIdx != null ? assessmentGroups[existingIdx] : undefined
    if (existingGroup) {
      existingGroup.parts.push(part)
    } else {
      groupIndexByEntity.set(entityId, assessmentGroups.length)
      assessmentGroups.push({
        entityId,
        isRoot: isRootEntity(entityId),
        parts: [part],
      })
    }
  }

  // Join with empty string: ADK chunks markdown across parts and the
  // chunks already carry their own whitespace. A `\n` separator breaks
  // multi-part tables and lists.
  const mergedRootText = rootTextParts.map(p => p.content).join("")

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {assessmentGroups.length > 0 && (
        <TopLevelAssessmentProcess
          groups={assessmentGroups}
          isRunning={!isComplete}
        />
      )}
      {mergedRootText && (
        <MarkdownContent>
          <ReactMarkdown
            remarkPlugins={[remarkMath, remarkGfm]}
            rehypePlugins={[rehypeKatex]}
          >
            {mergedRootText}
          </ReactMarkdown>
        </MarkdownContent>
      )}
    </Box>
  )
}
